import { observer } from 'mobx-react';
import React, { ReactNode, useEffect, useState } from 'react';
import { AgGridColumnProps, DataGrid, SelectFilterComponent, SelectFloatingFilterComponent, SelectMode, StandardDialog } from '@roc/ui';
import { sanitizeHtml } from '@roc/ui/utils';
import { useStore } from '@roc/client-portal-shared/hooks';
import { IconButton, Switch } from '@material-ui/core';
import { Layout } from '@roc/feature-app-core';
import { makeStyles } from '@material-ui/core/styles';
import { Edit } from '@material-ui/icons';
import { CreateTemplateModal } from 'apps/crm-portal/src/app/modules/ringCentral/createTemplate';
import { Theme } from '@material-ui/core/styles';



const noOpComparator = () => 0;

// local state flips immediately so the switch animates, then re-syncs with row data
const ActiveToggle = ({ isActive, onToggle }: { isActive: boolean; onToggle: () => Promise<boolean> }) => {
  const [checked, setChecked] = useState(isActive);
  useEffect(() => setChecked(isActive), [isActive]);
  const handleChange = async () => {
    setChecked(!checked);
    setChecked(await onToggle());
  };
  return (
    <span title={checked ? 'Deactivate' : 'Activate'}>
      <Switch size="small" color="primary" checked={checked} onChange={handleChange} />
    </span>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  gridContainer: {
    display: 'flex',
  },
  button: {
    margin: '10px',
    minWidth: '150px',
  },
}));

const columns: AgGridColumnProps[] = [
  {
    field: 'templateId',
    headerName: 'Template Id',
    minWidth: 150,
    maxWidth: 150,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'label',
    headerName: 'Label',
    minWidth: 160,
    maxWidth: 280,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 150,
    maxWidth: 150,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'value',
    headerName: 'Content',
    minWidth: 60,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    cellRenderer: 'contentRenderer',
    floatingFilter: true,
    hide: false,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'businessUnit',
    headerName: 'Business Unit',
    minWidth: 150,
    maxWidth: 150,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'subject',
    headerName: 'Subject',
    minWidth: 150,
    maxWidth: 150,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'fromJourneyBuilder',
    headerName: 'From Cadence',
    minWidth: 150,
    maxWidth: 150,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    valueFormatter: (params) => {
      return params.value != null ? params.value ? "Yes" : "No" : "No";
    }
  },
  {
    field: 'isActive',
    headerName: 'Active',
    minWidth: 120,
    maxWidth: 120,
    comparator: noOpComparator,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
    },
    valueFormatter: (params) => {
      return params.value === false ? 'Inactive' : 'Active';
    },
  },
  {
    field: 'activeAfter',
    headerName: 'Active After',
    minWidth: 170,
    maxWidth: 170,
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['greaterThan'], // backend treats the picked date as "on or after"
      suppressAndOrCondition: true,
    },
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'action',
    headerName: 'Action',
    minWidth: 150,
    maxWidth: 150,
    valueGetter: params => params.data?.isActive !== false, // makes ag-grid refresh the cell when isActive changes
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
  },
];

export const ManageTemplatesGrid = observer(({ toolbar }: { toolbar: ReactNode }) => {
  const { companyProfileStore, companyDropdownStore, ringCentralStore } = useStore();

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    ringCentralStore.templatesGrid.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    ringCentralStore.templatesGrid.setFilterModel(filterModel);
  };


  const handleOnclick = async (params) => {
    await companyDropdownStore.changeCompany(params.data?.companyId);
    companyProfileStore.setDisplayCompanyProfile(true);
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    const { templateId, label, value, type, subject, templateAttachments, bombBombVideoLink, imageInsertions, businessUnit, fromJourneyBuilder, activeAfter, isActive, ownerIds } = record;
    console.log(record);
    ringCentralStore.setTemplateId(templateId);
    ringCentralStore.setLabel(label);
    ringCentralStore.setTemplateValue(value);
    ringCentralStore.setTemplateSubject(subject);
    ringCentralStore.setTemplateType(type);
    ringCentralStore.setTemplateAttachments(templateAttachments);
    ringCentralStore.setImageInsertions(imageInsertions);
    ringCentralStore.setOpenCreateTemplateModal(true);
    ringCentralStore.setBombBombVideoLinkValue(bombBombVideoLink);
    ringCentralStore.setTemplateBusinessUnit(businessUnit)
    ringCentralStore.setTemplateOwners(ownerIds ?? [])
    ringCentralStore.setFromJourneyBuilder(fromJourneyBuilder)
    ringCentralStore.setActiveAfter(activeAfter)
    ringCentralStore.setIsTemplateActive(isActive)
  };

  const handleMenuClose = () => {
    ringCentralStore.setTemplateId('');
    ringCentralStore.setLabel('');
    ringCentralStore.setTemplateValue('');
    ringCentralStore.setTemplateType('');
    ringCentralStore.setTemplateSubject('');
    ringCentralStore.setTemplateAttachments([]);
    ringCentralStore.setOpenCreateTemplateModal(false);
    ringCentralStore.setBombBombVideoLinkValue('');
    ringCentralStore.setImageInsertions('');
    ringCentralStore.setTemplateBusinessUnit('');
    ringCentralStore.setTemplateOwners(null);
    ringCentralStore.setFromJourneyBuilder(false);
    ringCentralStore.setActiveAfter(null);
    ringCentralStore.setIsTemplateActive(true);
  };

  const getTextFromHTML = (htmlString: string) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = sanitizeHtml(htmlString);
    return tempElement.innerText
  }


  const toggleActive = async (params) => {
    const record = params.node.data;
    ringCentralStore.clearSelectedTemplates();
    ringCentralStore.setSelectedTemplate(record.templateId, true);
    await ringCentralStore.updateTemplatesActiveStatus(record.isActive === false);
    const row = ringCentralStore.templatesGrid.gridData.data.rows.find(r => r.templateId === record.templateId);
    return row?.isActive !== false;
  };

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    actionCellRenderer: params => {
      const isActive = params.node.data?.isActive !== false;
      return (
        <>
          <IconButton onClick={e => openMenu(e, params.node.data)} >
            <Edit color="primary" />
          </IconButton>
          <ActiveToggle isActive={isActive} onToggle={() => toggleActive(params)} />
        </>
      );
    },
    contentRenderer: ({ value }) => getTextFromHTML(value)
  };

  const onCreateTemplate = () => {
    ringCentralStore.setOpenCreateTemplateModal(true);
  }

  return (

    <Layout>
      <StandardDialog open={ringCentralStore.openCreateTemplateModal} title="Manage Text/Email Templates" maxWidth="xl" handleClose={handleMenuClose} dialogContent={<CreateTemplateModal handleClose={handleMenuClose} />} />
      <DataGrid
        selectMode={SelectMode.MULTIPLE}
        headerCheckboxSelection
        onRowSelected={e => ringCentralStore.setSelectedTemplate(e.data?.templateId, e.node.isSelected())}
        suppressRowClickSelection={true}
        frameworkComponents={frameworkComponents}
        toolbar={toolbar}
        columns={columns}
        rows={ringCentralStore.templatesGrid.gridData.data.rows}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={ringCentralStore.templatesGrid.isLoading}
        showFilters={ringCentralStore.templatesGrid.showFilters}
        sortModel={ringCentralStore.templatesGrid.sortModel}
        filterModel={ringCentralStore.templatesGrid.filterModel}
        paginationData={ringCentralStore.templatesGrid.gridData.meta}
        setPageNumber={ringCentralStore.templatesGrid.setPageNumber}
        rowHeight={60}
      />
    </Layout>
  );
});
